import { ActionIcon, Box, Combobox, Flex, Highlight, Loader, Paper, Text, TextInput, useCombobox } from '@mantine/core';
import type { ComboboxProps } from '@mantine/core';
import { XIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useDebounce } from 'use-debounce';

import type { Venue } from '../../../../types/DirectusSchema';
import { useVenueItemQuery } from '../../data/venues/useVenueItemQuery.ts';
import { useVenuesItemsQuery } from '../../data/venues/useVenuesItemsQuery.ts';
import { parseError } from '../reactHookFormMantine/lib/parseError.ts';

type VenueSelectProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<ComboboxProps, 'checked' | 'defaultValue'> & {
    onChange?: (optionValue: string) => void;
    label: string;
  };

export const VenueSelect = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  ...props
}: VenueSelectProps<T>) => {
  const {
    field: { value, onChange: fieldOnChange, ref, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 350);

  const { data: chosenVenue } = useVenueItemQuery(value);

  const { data: venues, isPending } = useVenuesItemsQuery({
    search: debouncedSearchText,
  });

  const options = venues?.map((venue: Venue) =>
    venue.id ? (
      <Combobox.Option key={venue.id} value={venue.id.toString(10)}>
        <Highlight fw="800" highlight={searchText} size="sm">
          {venue.commercial_name}
        </Highlight>
        <Highlight highlight={searchText} size="xs">{`${venue.address}, ${venue.postal_code} ${venue.city}`}</Highlight>
      </Combobox.Option>
    ) : (
      []
    ),
  );

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(optionValue) => {
        fieldOnChange(optionValue);
        onChange?.(optionValue);
        combobox.closeDropdown();
      }}
      {...field}
      {...props}
    >
      {!chosenVenue || chosenVenue.id === 0 ? (
        <>
          <Combobox.Target ref={ref}>
            <TextInput
              error={parseError('Venue', fieldState.error)}
              label="Pick venue"
              placeholder="Search venues"
              rightSection={isPending && <Loader size={18} />}
              value={searchText}
              onBlur={() => combobox.closeDropdown()}
              onChange={(event) => {
                setSearchText(event.currentTarget.value);
                combobox.resetSelectedOption();

                if (event.currentTarget.value.length > 0) {
                  combobox.openDropdown();
                } else {
                  combobox.closeDropdown();
                }
              }}
            />
          </Combobox.Target>
          <Combobox.Dropdown hidden={venues === null}>
            <Combobox.Options>
              {options}
              {venues && venues.length === 0 && <Combobox.Empty>No results found</Combobox.Empty>}
            </Combobox.Options>
          </Combobox.Dropdown>
        </>
      ) : (
        <Paper p="md">
          <Flex align="center" justify="space-between">
            <Box>
              <Text fw="800" size="sm">
                {chosenVenue.commercial_name}
              </Text>
              <Text size="xs">{`${chosenVenue.address}, ${chosenVenue.postal_code} ${chosenVenue.city}`}</Text>
            </Box>
            <ActionIcon
              color="red.5"
              size="xl"
              variant="subtle"
              onClick={() => {
                fieldOnChange?.('');
                setSearchText('');
              }}
            >
              <XIcon />
            </ActionIcon>
          </Flex>
        </Paper>
      )}
    </Combobox>
  );
};
